import { useMemo, useRef, useState } from 'react';
import {
  Clock3,
  Copy,
  Loader2,
  Trash2,
  Sunrise,
  Sun,
  Sunset,
  CheckCheck,
  RotateCcw,
  Minus,
  Plus,
  HelpCircle,
  ChevronDown,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import type { AvailableSlotHour, BookingSlot } from '@/features/campaigns/types/campaign';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { SlotCard } from './SlotCard';
import { useContentColumns } from '../useContentColumns';

type TimeBand = 'ALL' | 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT';

type HoursGridProps = {
  activeLabel: { weekday: string; dayMonth: string };
  isLoadingSlots: boolean;
  activeDayCount: number;
  hours: AvailableSlotHour[];
  applyingToAll?: boolean;
  appliedToAll?: boolean;
  onApplyToAll?: () => void;
  hideApplyToAll?: boolean;
  activeDate: string;
  defaultTotalReps: number;
  isSelected: (date: string, hour: number) => boolean;
  getSlot: (date: string, hour: number) => BookingSlot | undefined;
  onToggle: (
    date: string,
    hour: number,
    maxReps: number,
    syncAll?: boolean,
  ) => void;
  onSetReps: (
    date: string,
    hour: number,
    repetitions: number,
    syncAll?: boolean,
  ) => void;
  syncRepsAllDates?: boolean;
  setSyncRepsAllDates?: (sync: boolean) => void;
  activeScreenName?: string;
};

const BAND_TABS: Array<{ id: TimeBand; label: string; short: string }> = [
  { id: 'ALL', label: 'All 24 Hours', short: 'All' },
  { id: 'MORNING', label: 'Morning', short: 'AM' },
  { id: 'AFTERNOON', label: 'Afternoon', short: 'PM' },
  { id: 'EVENING', label: 'Evening', short: 'Eve' },
  { id: 'NIGHT', label: 'Night', short: 'Night' },
];

const PRESET_FREQ = [1, 5, 10, 20, 50];

export function HoursGrid({
  activeLabel,
  isLoadingSlots,
  activeDayCount,
  hours,
  applyingToAll = false,
  appliedToAll = false,
  onApplyToAll,
  hideApplyToAll = false,
  activeDate,
  defaultTotalReps,
  isSelected,
  getSlot,
  onToggle,
  onSetReps,
  syncRepsAllDates = false,
  setSyncRepsAllDates,
  activeScreenName,
}: HoursGridProps) {
  const alert = useManualAlertOptional();
  const [activeBand, setActiveBand] = useState<TimeBand>('ALL');
  const [bulkReps, setBulkReps] = useState(10);
  const [bulkUseMax, setBulkUseMax] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [bulkAppliedFlash, setBulkAppliedFlash] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const inventoryRef = useRef<HTMLDivElement>(null);

  const notifySuccess = (title: string, description: string) => {
    if (alert) {
      alert.success(title, description, {
        actions: [{ label: 'OK', primary: true }],
      });
      return;
    }
    toast.success(description);
  };

  const notifyInfo = (title: string, description: string) => {
    if (alert) {
      alert.info(title, description, {
        actions: [{ label: 'OK', primary: true }],
      });
      return;
    }
    toast.info(description);
  };
  const columns = useContentColumns(gridRef, 4);

  const selectedCapacity = useMemo(() => {
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    let count = 0;
    hours.forEach((slot) => {
      if (!isSelected(activeDate, slot.hour)) return;
      const avail = Math.max(1, slot.availableRepetitions ?? defaultTotalReps);
      min = Math.min(min, avail);
      max = Math.max(max, avail);
      count += 1;
    });
    if (count === 0) return null;
    return {
      count,
      min: Number.isFinite(min) ? min : 0,
      max,
    };
  }, [hours, activeDate, isSelected, defaultTotalReps]);

  const isHourInBand = (hour: number, band: TimeBand): boolean => {
    switch (band) {
      case 'MORNING':
        return hour >= 6 && hour < 12;
      case 'AFTERNOON':
        return hour >= 12 && hour < 18;
      case 'EVENING':
        return hour >= 18 && hour < 22;
      case 'NIGHT':
        return hour >= 22 || hour < 6;
      default:
        return true;
    }
  };

  const filteredHours = useMemo(() => {
    if (activeBand === 'ALL') return hours;
    return hours.filter((h) => isHourInBand(h.hour, activeBand));
  }, [hours, activeBand]);

  const handleSelectBand = (startH: number, endH: number, label: string) => {
    let added = 0;
    hours.forEach((slot) => {
      const inRange =
        startH <= endH
          ? slot.hour >= startH && slot.hour <= endH
          : slot.hour >= startH || slot.hour <= endH;
      const avail = slot.availableRepetitions ?? defaultTotalReps;
      if (inRange && avail > 0 && !isSelected(activeDate, slot.hour)) {
        onToggle(activeDate, slot.hour, Math.max(1, avail), syncRepsAllDates);
        added += 1;
      }
    });
    setQuickOpen(false);
    if (added > 0) {
      notifySuccess(
        'Hours booked',
        `Booked ${added} hour${added === 1 ? '' : 's'} · ${label}. Set plays below or tap Clear to undo.`,
      );
    } else {
      notifyInfo(
        'Nothing to book',
        'Those hours are already booked or unavailable.',
      );
    }
  };

  const handleSelectAll24 = () => {
    let added = 0;
    hours.forEach((slot) => {
      const avail = slot.availableRepetitions ?? defaultTotalReps;
      if (avail > 0 && !isSelected(activeDate, slot.hour)) {
        onToggle(activeDate, slot.hour, Math.max(1, avail), syncRepsAllDates);
        added += 1;
      }
    });
    setQuickOpen(false);
    if (added > 0) {
      notifySuccess(
        'Hours booked',
        `Booked ${added} hour${added === 1 ? '' : 's'} · All day. Set plays below or tap Clear to undo.`,
      );
    } else {
      notifyInfo(
        'Nothing to book',
        'All available hours are already booked.',
      );
    }
  };

  const handleClearDay = () => {
    hours.forEach((slot) => {
      if (isSelected(activeDate, slot.hour)) {
        onToggle(activeDate, slot.hour, 1, syncRepsAllDates);
      }
    });
    setQuickOpen(false);
  };

  const setBulkValue = (value: number) => {
    setBulkUseMax(false);
    setBulkReps(Math.max(1, value));
  };

  const handleApplyBulkReps = () => {
    let updated = 0;
    hours.forEach((slot) => {
      if (isSelected(activeDate, slot.hour)) {
        const max = Math.max(1, slot.availableRepetitions ?? defaultTotalReps);
        const next = bulkUseMax ? max : Math.min(bulkReps, max);
        onSetReps(activeDate, slot.hour, next, syncRepsAllDates);
        updated += 1;
      }
    });

    if (updated > 0) {
      const detail = bulkUseMax
        ? `each hour’s maximum remaining capacity`
        : `${bulkReps} play${bulkReps === 1 ? '' : 's'} each`;
      notifySuccess(
        'Plays updated',
        `Updated ${updated} hour${updated === 1 ? '' : 's'} to ${detail}${
          syncRepsAllDates ? ' (synced across dates)' : ''
        }.`,
      );
      setBulkAppliedFlash(true);
      window.setTimeout(() => setBulkAppliedFlash(false), 2200);
      inventoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <section className="rounded-xl border border-[#E6E8EF] bg-white text-left">
      {/* A. Workspace header */}
      <div className="flex flex-col gap-1.5 border-b border-[#EEF0F5] px-2.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:px-3.5 sm:py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Clock3 className="h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)]" />
            <h2 className="m-0 text-[12.5px] font-semibold text-[#1F2433] sm:text-[13px]">
              3. Book hours
            </h2>
            {activeScreenName ? (
              <span className="max-w-[40vw] truncate text-[11.5px] font-medium text-[#6B7289] sm:max-w-none sm:text-[12px]">
                · {activeScreenName}
              </span>
            ) : null}
          </div>
          <p className="m-0 mt-0.5 text-[11.5px] text-[#6B7289] sm:text-[12px]">
            <span className="font-medium text-[#1F2433]">
              {activeLabel.weekday} · {activeLabel.dayMonth}
            </span>
            <span className="mx-1.5 text-[#D0D4DE]">·</span>
            {isLoadingSlots
              ? 'Loading…'
              : activeDayCount > 0
                ? (
                  <>
                    <span className="sm:hidden">{activeDayCount} selected</span>
                    <span className="hidden sm:inline">
                      {activeDayCount} hour{activeDayCount === 1 ? '' : 's'}{' '}
                      selected — set plays on each card or use Apply below
                    </span>
                  </>
                )
                : 'Tap an hour to book'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label
            className="inline-flex min-h-[36px] cursor-pointer items-center gap-1.5 rounded-lg border border-[#E6E8EF] bg-[#F8F9FB] px-2 py-1 text-[11px] font-medium text-[#1F2433] select-none sm:px-2.5 sm:py-1.5 sm:text-[11.5px]"
            title="When on, booking an hour also books the same hour on every other campaign date for this display."
          >
            <span className="hidden sm:inline">Copy to all dates</span>
            <span className="sm:hidden">All dates</span>
            <HelpCircle className="hidden h-3 w-3 text-[#9CA3AF] sm:inline" aria-hidden />
            <span className="relative inline-flex">
              <input
                type="checkbox"
                className="sr-only"
                checked={syncRepsAllDates}
                onChange={(e) => setSyncRepsAllDates?.(e.target.checked)}
              />
              <span
                className={`relative block h-[18px] w-[32px] rounded-full transition-colors ${
                  syncRepsAllDates ? 'bg-[var(--dash-brand)]' : 'bg-[#D0D4DE]'
                }`}
              >
                <span
                  className={`absolute top-[2px] left-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform ${
                    syncRepsAllDates ? 'translate-x-[14px]' : ''
                  }`}
                />
              </span>
            </span>
          </label>

          {!hideApplyToAll && onApplyToAll ? (
            <button
              type="button"
              onClick={onApplyToAll}
              disabled={
                applyingToAll ||
                isLoadingSlots ||
                (!appliedToAll && activeDayCount === 0)
              }
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold transition disabled:opacity-40 ${
                appliedToAll
                  ? 'border-rose-200 text-rose-600 hover:bg-rose-50'
                  : 'border-[#E6E8EF] text-[var(--dash-brand)] hover:bg-[#F8F9FB]'
              }`}
            >
              {applyingToAll ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : appliedToAll ? (
                <Trash2 className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {appliedToAll ? 'Remove from all dates' : 'Apply to all dates'}
            </button>
          ) : null}
        </div>
      </div>

      {!isLoadingSlots && hours.length > 0 ? (
        <div className="space-y-1.5 border-b border-[#EEF0F5] px-2.5 py-2 sm:space-y-2 sm:px-3.5 sm:py-2.5">
          {/* Book actions + view filters */}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setQuickOpen((v) => !v)}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--dash-brand)]/30 bg-[var(--dash-brand-soft)]/50 px-2.5 py-1.5 text-[12px] font-semibold text-[var(--dash-brand)] transition hover:bg-[var(--dash-brand-soft)]"
                  aria-expanded={quickOpen}
                >
                  Quick book
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition ${quickOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {quickOpen ? (
                  <div className="absolute top-full left-0 z-30 mt-1 w-[min(100vw-2rem,15rem)] overflow-hidden rounded-xl border border-[#E6E8EF] bg-white py-1 shadow-lg sm:w-60">
                    <p className="m-0 px-3 py-1.5 text-[10px] font-bold tracking-wide text-[#9CA3AF] uppercase">
                      Select a peak window
                    </p>
                    <button
                      type="button"
                      onClick={() => handleSelectBand(6, 11, 'Morning Peak')}
                      className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-medium text-[#1F2433] hover:bg-[#F8F9FB]"
                    >
                      <Sunrise className="h-3.5 w-3.5 text-amber-600" />
                      Morning Peak · 6–11 AM
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectBand(11, 17, 'Business Prime')}
                      className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-medium text-[#1F2433] hover:bg-[#F8F9FB]"
                    >
                      <Sun className="h-3.5 w-3.5 text-sky-600" />
                      Business Prime · 11 AM–5 PM
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectBand(17, 22, 'Evening Peak')}
                      className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-medium text-[#1F2433] hover:bg-[#F8F9FB]"
                    >
                      <Sunset className="h-3.5 w-3.5 text-[var(--dash-accent)]" />
                      Evening Peak · 5–10 PM
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectAll24}
                      className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-medium text-[#1F2433] hover:bg-[#F8F9FB]"
                    >
                      <CheckCheck className="h-3.5 w-3.5 text-emerald-600" />
                      All 24 Hours
                    </button>
                  </div>
                ) : null}
              </div>

              {activeDayCount > 0 ? (
                <button
                  type="button"
                  onClick={handleClearDay}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  <span className="sm:hidden">Clear</span>
                  <span className="hidden sm:inline">Clear selection</span>
                </button>
              ) : null}
            </div>

            <div
              role="radiogroup"
              aria-label="Filter visible hours"
              className="inline-flex w-full gap-0.5 overflow-x-auto rounded-lg border border-[#E6E8EF] bg-[#F8F9FB] p-0.5 [scrollbar-width:none] sm:ml-auto sm:w-auto sm:overflow-visible [&::-webkit-scrollbar]:hidden"
            >
              {BAND_TABS.map((tab) => {
                const active = activeBand === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setActiveBand(tab.id)}
                    className={`min-h-[32px] shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold transition ${
                      active
                        ? 'bg-white text-[#1F2433] shadow-sm'
                        : 'text-[#6B7289] hover:text-[#1F2433]'
                    }`}
                  >
                    <span className="sm:hidden">{tab.short}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <p className="m-0 hidden text-[10.5px] text-[#9CA3AF] sm:block">
            Filters only change which hours you see. They never book or clear
            slots.
          </p>
        </div>
      ) : null}

      <div className="p-2.5 sm:p-3.5" ref={gridRef}>
        {!isLoadingSlots && activeDayCount === 0 && hours.length > 0 ? (
          <div className="mb-2.5 rounded-xl border border-dashed border-[var(--dash-brand)]/30 bg-[var(--dash-brand-soft)]/35 px-2.5 py-2 sm:mb-3 sm:px-3 sm:py-2.5">
            <p className="m-0 text-[12px] font-semibold text-[#1F2433] sm:text-[12.5px]">
              How to book
            </p>
            <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-[#6B7289] sm:text-[11.5px]">
              <span className="sm:hidden">
                Tap a green hour, or use Quick book for peaks.
              </span>
              <span className="hidden sm:inline">
                Tap any green/amber hour below to select it, then set plays on
                the card. Or use{' '}
                <span className="font-semibold">Quick book</span> for Morning /
                Evening peaks in one click.
              </span>
            </p>
          </div>
        ) : null}

        {/* Bulk plays bar — sits with inventory so Apply results are visible */}
        {!isLoadingSlots && activeDayCount > 0 ? (
          <div
            className={`mb-3 rounded-xl border px-3 py-2.5 transition-colors ${
              bulkAppliedFlash
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-[var(--dash-brand)]/25 bg-[var(--dash-brand-soft)]/50'
            }`}
          >
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <p className="m-0 text-[12.5px] font-semibold text-[#1F2433]">
                  {bulkAppliedFlash ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-800">
                      <Check className="h-3.5 w-3.5" />
                      Plays updated on selected hours
                    </span>
                  ) : (
                    <>
                      Set plays for{' '}
                      <span className="tabular-nums text-[var(--dash-brand)]">
                        {activeDayCount}
                      </span>{' '}
                      selected hour{activeDayCount === 1 ? '' : 's'}
                    </>
                  )}
                </p>
                <p className="m-0 mt-0.5 text-[11px] text-[#6B7289]">
                  {bulkUseMax ? (
                    <>
                      Max uses each hour’s remaining capacity
                      {selectedCapacity
                        ? selectedCapacity.min === selectedCapacity.max
                          ? ` (${selectedCapacity.max} plays)`
                          : ` (${selectedCapacity.min}–${selectedCapacity.max} plays)`
                        : ''}
                      {syncRepsAllDates ? ' · synced dates included' : ''}.
                    </>
                  ) : (
                    <>
                      Choose a fixed play count or Max, then apply to every
                      selected hour below
                      {syncRepsAllDates ? ' (and synced dates)' : ''}.
                    </>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={bulkUseMax || bulkReps <= 1}
                    onClick={() => setBulkValue(bulkReps - 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E6E8EF] bg-white disabled:opacity-30"
                    aria-label="Decrease plays"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={bulkUseMax ? '' : bulkReps}
                    placeholder={bulkUseMax ? 'Max' : undefined}
                    onChange={(e) => setBulkValue(Number(e.target.value) || 1)}
                    className={`h-8 w-14 rounded-lg border bg-white px-1 text-center text-[13px] font-semibold tabular-nums outline-none ${
                      bulkUseMax
                        ? 'border-[var(--dash-brand)]/40 text-[var(--dash-brand)] placeholder:text-[var(--dash-brand)]'
                        : 'border-[#E6E8EF]'
                    }`}
                    aria-label="Plays to apply"
                  />
                  <button
                    type="button"
                    disabled={bulkUseMax}
                    onClick={() => setBulkValue(bulkReps + 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E6E8EF] bg-white disabled:opacity-30"
                    aria-label="Increase plays"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                {PRESET_FREQ.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setBulkValue(p)}
                    className={`rounded-md px-2 py-1.5 text-[11px] font-bold ${
                      !bulkUseMax && bulkReps === p
                        ? 'bg-[var(--dash-brand)] text-white'
                        : 'border border-[#E6E8EF] bg-white text-[#6B7289]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setBulkUseMax(true)}
                  className={`rounded-md px-2 py-1.5 text-[11px] font-bold ${
                    bulkUseMax
                      ? 'bg-[var(--dash-brand)] text-white'
                      : 'border border-[#E6E8EF] bg-white text-[#6B7289]'
                  }`}
                  title={
                    selectedCapacity
                      ? selectedCapacity.min === selectedCapacity.max
                        ? `Use max remaining capacity (${selectedCapacity.max})`
                        : `Use each hour’s max (${selectedCapacity.min}–${selectedCapacity.max})`
                      : 'Use each hour’s maximum remaining capacity'
                  }
                >
                  Max
                  {selectedCapacity
                    ? selectedCapacity.min === selectedCapacity.max
                      ? ` (${selectedCapacity.max})`
                      : ''
                    : ''}
                </button>
                <button
                  type="button"
                  onClick={handleApplyBulkReps}
                  className="rounded-lg bg-[var(--dash-brand)] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:brightness-105"
                >
                  {bulkUseMax
                    ? `Apply Max to ${activeDayCount}`
                    : `Apply ${bulkReps} plays to ${activeDayCount} slots`}
                    {/* : `Apply ${bulkReps} plays to ${activeDayCount} slots`} */}
                </button>
                <button
                  type="button"
                  onClick={handleClearDay}
                  className="rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div
          ref={inventoryRef}
          className="mb-2 flex items-center justify-between gap-2"
        >
          <p className="m-0 text-[11px] font-medium text-[#9CA3AF]">
            Hourly inventory
          </p>
          <div className="flex items-center gap-2.5 text-[10px] font-medium text-[#6B7289]">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Available
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Limited
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              Full
            </span>
          </div>
        </div>

        {isLoadingSlots ? (
          <div
            className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E6E8EF] bg-[#F8F9FB] px-4 py-10"
            role="status"
            aria-busy="true"
          >
            <Loader2 className="mb-2 h-5 w-5 animate-spin text-[var(--dash-brand)]" />
            <p className="m-0 text-[13px] font-semibold text-[#1F2433]">
              Loading slot inventory…
            </p>
          </div>
        ) : filteredHours.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E6E8EF] bg-[#F8F9FB] px-4 py-8 text-center">
            <p className="m-0 text-[13px] font-semibold text-[#1F2433]">
              No hours in this period
            </p>
            <p className="mt-1 mb-0 text-[12px] text-[#6B7289]">
              Switch the filter to All 24 Hours — bookings are unchanged.
            </p>
          </div>
        ) : (
          <div
            className="grid gap-2.5"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {filteredHours.map((slot) => (
              <SlotCard
                key={slot.hour}
                date={activeDate}
                slot={slot}
                selected={isSelected(activeDate, slot.hour)}
                current={getSlot(activeDate, slot.hour)}
                defaultTotalReps={defaultTotalReps}
                onToggle={(d, h, m) => onToggle(d, h, m, syncRepsAllDates)}
                onSetReps={(d, h, r) => onSetReps(d, h, r, syncRepsAllDates)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
